import { AlertCircle, ClipboardList, Coins, MessageSquare, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorState } from "@/components/feedback/ErrorState";
import { SectionCard } from "@/components/ui/section-card";
import { StatCard } from "@/components/ui/stat-card";
import { useObservabilityStatus } from "@/features/observability/hooks/useObservability";
import { useUserUsage } from "@/features/service-users/hooks/useUserUsage";
import { formatCostUsd } from "@/lib/cost";

export function ServiceUserUsagePanel({
  serviceUserId,
}: {
  serviceUserId: string;
}) {
  const { t } = useTranslation("serviceUsers");
  const status = useObservabilityStatus();
  const usage = useUserUsage(serviceUserId);
  const costEnabled = status.data?.enabled ?? true;

  if (usage.isError) {
    return (
      <SectionCard title={t("usagePanel.title")}>
        <ErrorState
          error={usage.error}
          onRetry={() => void usage.refetch()}
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={t("usagePanel.title")}
      description={t("usagePanel.description")}
    >
      {!costEnabled ? (
        <div className="flex items-start gap-2 rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700" />
          <span>{t("usagePanel.costDisabled")}</span>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("usagePanel.kpi.cost")}
          value={usage.data ? formatCostUsd(usage.data.total_cost) : "—"}
          icon={Coins}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("usagePanel.kpi.threads")}
          value={usage.data?.thread_count.toLocaleString("pt-BR") ?? "—"}
          icon={MessageSquare}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("usagePanel.kpi.tasks")}
          value={usage.data?.task_count.toLocaleString("pt-BR") ?? "—"}
          icon={ClipboardList}
          loading={usage.isLoading}
        />
        <StatCard
          label={t("usagePanel.kpi.tokens")}
          value={usage.data?.total_tokens.toLocaleString("pt-BR") ?? "—"}
          icon={Sparkles}
          loading={usage.isLoading}
        />
      </div>
    </SectionCard>
  );
}
